// Post-Aggiudicazione kanban — drag participations across 12 workflow stages.
// Data: GET /participations?status=aggiudicata grouped client-side by
// post_agg_stage. Drop on a column → PATCH /participations/{id} with the new
// stage (optimistic update + rollback on error).
//
// This complements /pipeline (SCD workflow); SCD ends at "Aggiudicata",
// Post-Agg starts where SCD ends.
import { useMemo, useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { hasTokens } from '../lib/auth'
import {
  ApiError,
  fetchParticipations,
  patchParticipation,
  type ParticipationListResponse,
  type ParticipationRow,
  type PostAggStage,
} from '../lib/api'
import { useMe } from '../hooks/useAuth'
import { canWriteParticipation } from '../lib/permissions'
import { CardSkeleton } from '../components/Skeleton'

export const Route = createFileRoute('/pipeline/post-agg')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  component: PostAggKanbanPage,
})

// Column order mirrors workflow progression. `null` (no stage yet) goes first
// so freshly aggiudicate appear immediately. "non_saldate" is parked at the
// far right (failure terminal state).
const STAGES: Array<{ key: PostAggStage | 'unassigned'; label: string; tone: string }> = [
  { key: 'unassigned', label: 'Senza stage', tone: 'neutral' },
  { key: 'contatti_delegati', label: 'Contatti delegati', tone: 'info' },
  { key: 'sospesa', label: 'Sospesa', tone: 'warn' },
  { key: 'richiedere_verbale', label: 'Richiedere verbale', tone: 'info' },
  { key: 'richiesta_verbale_liberazione', label: 'Verbale liberazione', tone: 'info' },
  { key: 'verbale_ricevuto', label: 'Verbale ricevuto', tone: 'info' },
  { key: 'conteggi_saldo', label: 'Conteggi saldo', tone: 'warn' },
  { key: 'saldo_pagato', label: 'Saldo pagato', tone: 'ok' },
  { key: 'dt_firmato', label: 'DT firmato', tone: 'ok' },
  { key: 'attesa_liberazione', label: 'Attesa liberazione', tone: 'warn' },
  { key: 'consegna', label: 'Consegna', tone: 'ok' },
  { key: 'conclusa', label: 'Conclusa', tone: 'ok' },
  { key: 'non_saldate', label: 'Non saldate', tone: 'risk' },
]

type ColumnData = {
  key: PostAggStage | 'unassigned'
  label: string
  tone: string
  cards: ParticipationRow[]
}

function fmtMoneyShort(v: number | null): string {
  if (v === null || v === undefined) return '—'
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `€${(v / 1_000).toFixed(0)}k`
  return `€${v}`
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const ms = Date.now() - d.getTime()
  return Math.floor(ms / (24 * 60 * 60 * 1000))
}

function shortCodePA(n: number | null): string {
  if (n === null || n === undefined) return ''
  return `PA_${n.toString().padStart(5, '0')}`
}

function DraggableCard({
  p,
  disabled,
  onOpen,
}: {
  p: ParticipationRow
  disabled: boolean
  onOpen: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: p.id,
      data: { participation: p },
      disabled,
    })
  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.4 : 1,
    cursor: disabled ? 'pointer' : isDragging ? 'grabbing' : 'grab',
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-sm)',
    padding: 'var(--sp-2)',
    fontSize: 'var(--fs-12)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  }
  const days = daysSince(p.data_aggiudicazione)
  const clientLabel = p.client_name ?? '—'
  const lotLabel = p.property_address ?? p.property_comune ?? 'Lotto'
  const code = shortCodePA(p.short_code)
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      onClick={(e) => {
        // Only open if not currently a drag attempt; the activationConstraint
        // (distance 6) prevents click-firing during drag.
        if (!isDragging) {
          e.stopPropagation()
          onOpen()
        }
      }}
      aria-label={`Partecipazione ${code}`}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span className="tag code" style={{ fontSize: 'var(--fs-10)' }}>
          {code}
        </span>
        {days !== null && (
          <span
            className="muted"
            style={{ fontSize: 'var(--fs-10)', marginLeft: 'auto' }}
          >
            +{days}gg
          </span>
        )}
      </div>
      <div style={{ fontWeight: 'var(--fw-medium)' }}>{clientLabel}</div>
      <div className="muted" style={{ fontSize: 'var(--fs-11)' }}>
        {lotLabel}
      </div>
      <div style={{ fontSize: 'var(--fs-11)', color: 'var(--ink-2)' }}>
        Agg. {fmtMoneyShort(p.prezzo_aggiudicazione)}
      </div>
    </div>
  )
}

function DroppableColumn({
  col,
  disabled,
  onOpen,
}: {
  col: ColumnData
  disabled: boolean
  onOpen: (id: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: col.key,
    data: { stage: col.key },
  })
  return (
    <div
      ref={setNodeRef}
      className={`kanban-col ${isOver ? 'drop-over' : ''}`}
      style={{
        background: 'var(--bg-2)',
        border: `1px solid ${isOver ? 'var(--accent-ink)' : 'var(--line)'}`,
        borderRadius: 'var(--r-md)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 260,
        maxHeight: '75vh',
        transition: 'border-color var(--dur-fast)',
      }}
      aria-label={`Colonna ${col.label}`}
    >
      <div
        style={{
          padding: 'var(--sp-2) var(--sp-3)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          background: 'var(--surface)',
          borderRadius: 'var(--r-md) var(--r-md) 0 0',
        }}
      >
        <span
          style={{
            fontSize: 'var(--fs-12)',
            fontWeight: 'var(--fw-semibold)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {col.label}
        </span>
        <span
          className={`chip ${col.tone}`}
          style={{
            fontSize: 'var(--fs-11)',
            padding: '1px 6px',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {col.cards.length}
        </span>
      </div>
      <div
        style={{
          padding: 'var(--sp-2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          overflow: 'auto',
          flex: 1,
        }}
      >
        {col.cards.map((p) => (
          <DraggableCard
            key={p.id}
            p={p}
            disabled={disabled}
            onOpen={() => onOpen(p.id)}
          />
        ))}
        {col.cards.length === 0 && (
          <div
            className="muted"
            style={{
              padding: 'var(--sp-3)',
              fontSize: 'var(--fs-11)',
              fontStyle: 'italic',
              textAlign: 'center',
            }}
          >
            Vuoto
          </div>
        )}
      </div>
    </div>
  )
}

function PostAggKanbanPage() {
  const navigate = useNavigate()
  const { data: me } = useMe()
  const qc = useQueryClient()
  const canEdit = canWriteParticipation(me)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  const queryKey = ['participations', 'post-agg']
  const { data, isLoading } = useQuery<ParticipationListResponse, ApiError>({
    queryKey,
    queryFn: () =>
      fetchParticipations({
        status: 'aggiudicata',
        page_size: 300,
        sort: '-data_aggiudicazione',
      }),
    enabled: hasTokens(),
    staleTime: 60_000,
    retry: false,
  })

  const [activeCard, setActiveCard] = useState<ParticipationRow | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  const columns: ColumnData[] = useMemo(() => {
    const items = data?.items ?? []
    const byStage = new Map<string, ParticipationRow[]>()
    for (const meta of STAGES) {
      byStage.set(String(meta.key), [])
    }
    for (const p of items) {
      const key = p.post_agg_stage ?? 'unassigned'
      const bucket = byStage.get(String(key))
      if (bucket) bucket.push(p)
      else byStage.get('unassigned')?.push(p)
    }
    return STAGES.map((meta) => ({
      key: meta.key,
      label: meta.label,
      tone: meta.tone,
      cards: byStage.get(String(meta.key)) ?? [],
    }))
  }, [data])

  const moveMut = useMutation<unknown, ApiError, { id: string; to: PostAggStage | null }>({
    mutationFn: ({ id, to }) =>
      patchParticipation(id, { post_agg_stage: to as PostAggStage }),
    onMutate: async ({ id, to }) => {
      await qc.cancelQueries({ queryKey })
      const prev = qc.getQueryData<ParticipationListResponse>(queryKey)
      if (prev) {
        const next: ParticipationListResponse = {
          ...prev,
          items: prev.items.map((p) =>
            p.id === id ? { ...p, post_agg_stage: to } : p,
          ),
        }
        qc.setQueryData(queryKey, next)
      }
      return { prev }
    },
    onError: (err, _vars, ctx) => {
      const c = ctx as { prev?: ParticipationListResponse } | undefined
      if (c?.prev) qc.setQueryData(queryKey, c.prev)
      setMoveError(err?.message ?? 'Errore spostamento')
      setTimeout(() => setMoveError(null), 4000)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey })
    },
  })

  function onDragStart(e: DragStartEvent) {
    setActiveCard((e.active.data.current?.participation as ParticipationRow) ?? null)
    setMoveError(null)
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveCard(null)
    if (!e.over) return
    const p = e.active.data.current?.participation as ParticipationRow | undefined
    const to = e.over.id as PostAggStage | 'unassigned'
    if (!p) return
    const currentStage = p.post_agg_stage ?? 'unassigned'
    if (currentStage === to) return
    moveMut.mutate({ id: p.id, to: to === 'unassigned' ? null : to })
  }

  const total = data?.items.length ?? 0

  return (
    <>
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--accent-border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-2) var(--sp-3)',
          margin: '0 0 var(--sp-3) 0',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          flexWrap: 'wrap',
          fontSize: 'var(--fs-12)',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--fs-18)',
            fontWeight: 'var(--fw-semibold)',
          }}
        >
          📦 Pipeline Post-Aggiudicazione
        </h1>
        <span>
          <b>{total}</b> partecipazioni aggiudicate
        </span>
        {!canEdit && <span className="chip outline">Sola lettura</span>}
        {moveMut.isPending && <span className="chip info">Sposto…</span>}
        {moveError && (
          <span className="chip risk" role="alert">
            {moveError}
          </span>
        )}
      </div>

      {isLoading && <CardSkeleton />}

      {data && (
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <div
            className="kanban-board"
            style={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: 'minmax(260px, 1fr)',
              gap: 'var(--sp-3)',
              overflowX: 'auto',
              paddingBottom: 'var(--sp-2)',
            }}
          >
            {columns.map((c) => (
              <DroppableColumn
                key={String(c.key)}
                col={c}
                disabled={!canEdit || moveMut.isPending}
                onOpen={(id) =>
                  navigate({ to: '/partecipazioni/$id', params: { id } } as never)
                }
              />
            ))}
          </div>
          <DragOverlay>
            {activeCard && (
              <div
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--accent-ink)',
                  borderRadius: 'var(--r-sm)',
                  padding: 'var(--sp-2)',
                  fontSize: 'var(--fs-12)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  cursor: 'grabbing',
                }}
              >
                <div style={{ fontWeight: 'var(--fw-medium)' }}>
                  {activeCard.client_name ?? '—'}
                </div>
                <div className="muted" style={{ fontSize: 'var(--fs-11)' }}>
                  {shortCodePA(activeCard.short_code)}
                </div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </>
  )
}
